import { DecisionContext, defaultFinancialYear } from "@/dbot/common/commonDomain";
import { makeAutoObservable } from "mobx";
import { getFullName, sortedBoard } from "@/utils/Roles";
import { isBefore } from "@/utils/Dates";

import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import { BooleanFieldModel, LocalDateFieldModel, NumberFieldModel } from "@/common/commonForms";

export type ApproveCompanyBoardRemuneration = {
  type: DecisionType.approveCompanyBoardRemuneration;
  isReceive?: boolean;
  isDeviatingPeriod?: boolean;
  isCurrentYear?: boolean;
  period?: string;
  deviatingPeriodFrom?: string;
  deviatingPeriodTo?: string;
  remuneration?: Array<Remuneration>;
};

export type Remuneration = {
  id: string;
  name: string;
  value: number;
};

export type RemunerationModel = {
  id: string;
  name: string;
  value: NumberFieldModel;
};

export type RemunerationViewModel = RemunerationModel & {
  isSelected: boolean;
};

function mapToModel(remuneration: Remuneration): RemunerationModel {
  return {
    id: remuneration.id,
    name: remuneration.name,
    value: new NumberFieldModel({
      value: remuneration.value,
      required: true,
      isPositiveOrZero: true,
    }),
  };
}

export function remunerationModels({
  context,
  remunerationByRoleId,
}: {
  context: DecisionContext<ApproveCompanyBoardRemuneration>;
  remunerationByRoleId: Map<string, RemunerationModel>;
}): RemunerationModel[] {
  return sortedBoard(context.initialState.roles).map(
    (m) =>
      remunerationByRoleId.get(String(m.companyroleId)) ?? {
        id: String(m.companyroleId),
        name: getFullName(m),
        value: new NumberFieldModel({ required: true, isPositiveOrZero: true }),
      },
  );
}

export class ApproveCompanyBoardRemunerationForm {
  readonly type = DecisionType.approveCompanyBoardRemuneration;
  isReceive: BooleanFieldModel; // will board members receive any payment?
  isDeviatingPeriod: BooleanFieldModel;
  isCurrentYear: BooleanFieldModel;
  remunerationByRoleId: Map<string, RemunerationModel>;
  deviatingPeriodFrom: LocalDateFieldModel;
  deviatingPeriodTo: LocalDateFieldModel;
  boardNotSelectedWarning?: TMessage;
  deviatingPeriodSuitableWarning?: TMessage;
  period: string;

  constructor(readonly context: DecisionContext<ApproveCompanyBoardRemuneration>) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.isReceive = new BooleanFieldModel({
      value: context.decisionData.isReceive,
      required: true,
      onChange: (val) => this.isCurrentYear.setRequired(val),
    });
    this.isDeviatingPeriod = new BooleanFieldModel({
      value: this.context.decisionData.isDeviatingPeriod,
      required: true,
    });
    this.isCurrentYear = new BooleanFieldModel({
      value: this.context.decisionData.isCurrentYear,
      required: this.context.decisionData.isReceive,
      onChange: (val) => {
        this.deviatingPeriodFrom.setRequired(!val);
        this.deviatingPeriodTo.setRequired(!val);
      },
    });
    this.period = this.context.decisionData.period ?? defaultFinancialYear();
    this.remunerationByRoleId = new Map(
      (this.context.decisionData.remuneration ?? []).map((r) => [r.id, mapToModel(r)]),
    );
    this.deviatingPeriodFrom = new LocalDateFieldModel({
      value: this.context.decisionData.deviatingPeriodFrom,
      required:
        this.context.decisionData.isReceive && this.context.decisionData.isCurrentYear === false,
    });
    this.deviatingPeriodTo = new LocalDateFieldModel({
      value: this.context.decisionData.deviatingPeriodTo,
      required:
        this.context.decisionData.isReceive && this.context.decisionData.isCurrentYear === false,
    });
  }

  get selectedRemunerations(): RemunerationModel[] {
    return Array.from(this.remunerationByRoleId.values());
  }

  isSelected(r: RemunerationModel): boolean {
    return this.remunerationByRoleId.has(r.id);
  }

  toggleRemuneration(r: RemunerationModel) {
    if (this.remunerationByRoleId.has(r.id)) {
      this.remunerationByRoleId.delete(r.id);
    } else {
      this.remunerationByRoleId.set(r.id, r);
    }
  }

  get showRemunerations() {
    return this.isReceive.value === true;
  }

  get showPeriod() {
    return this.showRemunerations && this.isCurrentYear.value === false;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isRemunerationValid(): boolean {
    if (this.showRemunerations) {
      return (
        this.selectedRemunerations.length > 0 &&
        this.selectedRemunerations.every((r) => r.value.isValid)
      );
    }
    return true;
  }

  validateRemunerations() {
    this.boardNotSelectedWarning = undefined;
    if (this.showRemunerations) {
      this.selectedRemunerations.forEach((d) => d.value.validate());
      if (this.selectedRemunerations.length === 0) {
        this.boardNotSelectedWarning = {
          key: "decisions.approveCompanyBoardRemuneration.receive-remuneration.warning",
        };
      }
    }
  }

  validateDeviatingPeriod(): boolean {
    this.deviatingPeriodSuitableWarning = undefined;
    if (this.showPeriod) {
      this.deviatingPeriodFrom.validate();
      this.deviatingPeriodTo.validate();
      if (
        this.deviatingPeriodFrom.isValid &&
        this.deviatingPeriodTo.isValid &&
        !isBefore(this.deviatingPeriodFrom.inputValue, this.deviatingPeriodTo.inputValue)
      ) {
        this.deviatingPeriodSuitableWarning = {
          key: "decisions.approveCompanyBoardRemuneration.enter-period.warning",
        };
      }
    }
    return true;
  }

  get isValidDeviatingPeriod() {
    if (this.showPeriod) {
      return (
        !this.deviatingPeriodSuitableWarning &&
        this.deviatingPeriodFrom.isValid &&
        this.deviatingPeriodTo.isValid
      );
    }
    return true;
  }

  get isValid() {
    return (
      this.isReceive.isValid &&
      this.isCurrentYear.isValid &&
      this.isRemunerationValid &&
      this.isValidDeviatingPeriod
    );
  }

  validate(): boolean {
    this.isReceive.validate();
    this.isCurrentYear.validate();
    this.validateRemunerations();
    this.validateDeviatingPeriod();
    return this.isValid;
  }
}
